import React, { useEffect, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Users, MapPin, Package, RefreshCw } from "lucide-react";

const AdminMarketAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/market-alerts");
      setAlerts(r.data || []);
    } catch (e) {
      toast.error(arabicError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-market-alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={32} /> تنبيهات إشباع السوق
          </h1>
          <p className="text-[#666666] mt-1">المنتجات التي أصبحت متوفرة عند 10 تجار أو أكثر في نفس المحافظة (كل تاجر ≥ 3500 متر)</p>
        </div>
        <button onClick={load} data-testid="refresh-alerts" className="h-11 px-4 rounded-lg border-2 border-[#09637E] text-[#09637E] font-bold hover:bg-[#EBF4F6] flex items-center gap-2">
          <RefreshCw size={16} /> تحديث
        </button>
      </div>

      {loading ? (
        <div className="text-center text-[#666666] py-12">جارٍ التحميل...</div>
      ) : alerts.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-2xl p-12 text-center" data-testid="no-alerts">
          <Package className="mx-auto text-[#7AB2B2] mb-3" size={48} />
          <p className="text-[#666666] font-bold text-lg">لا توجد تنبيهات إشباع سوق حالياً</p>
          <p className="text-sm text-[#666666] mt-2">ستظهر التنبيهات هنا تلقائياً عند حدوث إشباع في السوق</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="alerts-list">
          {alerts.map((a) => {
            const fp = a.fingerprint || {};
            const product = [fp.category, fp.type, fp.country, fp.size, fp.color, fp.manufacturer].filter(Boolean).join(" · ");
            const isOpen = expanded === a.key;
            return (
              <div key={a.key} className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden" data-testid={`alert-${a.key}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : a.key)}
                  className="w-full p-4 flex items-center justify-between hover:bg-[#EBF4F6] text-right"
                  data-testid={`alert-toggle-${a.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-extrabold px-2 py-1 rounded ${a.alert_type === "shortage" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                        {a.alert_type === "shortage" ? "نقص في السوق 📈" : "تشبع في السوق 📉"}
                      </span>
                      <span className="bg-amber-100 text-amber-800 text-xs font-extrabold px-2 py-1 rounded flex items-center gap-1">
                        <MapPin size={12} /> {a.city}
                      </span>
                      <span className="bg-[#09637E] text-white text-xs font-extrabold px-2 py-1 rounded flex items-center gap-1">
                        <Users size={12} /> {a.alert_type === "shortage" ? `${a.last_count} باحثون` : `${a.last_count || (a.notified_ids || []).length} تجار`}
                      </span>
                    </div>
                    <div className="font-bold text-[#333333] mt-2 break-words">{product}</div>
                    {a.updated_at && (
                      <div className="text-xs text-[#666666] mt-1">آخر تحديث: {new Date(a.updated_at).toLocaleString("ar-SY")}</div>
                    )}
                  </div>
                </button>
                {isOpen && a.notified_merchants && (
                  <div className="border-t border-[#EBF4F6] p-4 bg-[#EBF4F6]/40">
                    <div className="text-sm font-bold text-[#333333] mb-2">التجار الذين تم إشعارهم ({a.notified_merchants.length}):</div>
                    <ul className="text-sm space-y-1" data-testid={`merchants-list-${a.key}`}>
                      {a.notified_merchants.map((m, i) => (
                        <li key={m.id || m.email || `merchant-${i}`} className="flex items-center justify-between bg-white p-2 rounded border border-[#EBF4F6]">
                          <span className="font-bold">{m.store_name || m.email || "—"}</span>
                          <span className="text-xs text-[#666666]">{m.email}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminMarketAlerts;
